import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, RefreshCw, Inbox } from "lucide-react";
import { fetchProspects } from "@/lib/api";
import { type ProspectStatus, PROSPECT_STATUS_LABELS } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ProspectTile } from "@/components/ProspectTile";

const FILTER_TABS: { value: ProspectStatus | "all" | "review"; label: string }[] = [
  { value: "review", label: "À reviewer" },
  { value: "ready", label: "Prêts" },
  { value: "sent", label: "Envoyés" },
  { value: "all", label: "Tous" },
];
const REVIEW_STATUSES: ProspectStatus[] = ["awaiting_review", "edited"];

// Rafraîchissement par polling (remplace le realtime Supabase `postgres_changes`) : n8n écrit en base
// de façon asynchrone, on re-tire la liste périodiquement pour rester proche du temps réel.
const POLL_INTERVAL_MS = 20_000;

export function Prospects() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ProspectStatus | "all" | "review">("review");

  const {
    data: prospects = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["prospects"],
    queryFn: fetchProspects,
    refetchInterval: POLL_INTERVAL_MS,
  });

  // Compteurs par statut pour les onglets
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: prospects.length, review: 0 };
    for (const p of prospects) {
      c[p.status] = (c[p.status] ?? 0) + 1;
      if (REVIEW_STATUSES.includes(p.status)) c.review += 1;
    }
    return c;
  }, [prospects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return prospects.filter((p) => {
      if (filter === "review" && !REVIEW_STATUSES.includes(p.status)) return false;
      if (filter !== "review" && filter !== "all" && p.status !== filter) return false;
      if (!q) return true;
      const haystack = [
        p.first_name,
        p.last_name,
        p.email,
        p.company_name,
        p.industry,
        p.personalization_subject,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [prospects, search, filter]);

  return (
    <div className="flex h-full flex-col">
      {/* === En-tête de page (style Aurora .entry-header) === */}
      <div className="aurora-hero-gradient px-8 py-8 text-white">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-3xl font-bold tracking-tight">Prospects</h1>
          <p className="mt-1 text-sm text-white/75">
            Pipeline de prospection cold email — review et validation des accroches générées par
            Claude
          </p>
        </div>
      </div>

      {/* === Toolbar : filtres + recherche === */}
      <div className="border-b border-neutral-200 bg-white px-8 py-4">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Onglets filtres */}
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-neutral-200 bg-neutral-50 p-1">
            {FILTER_TABS.map((tab) => {
              const isActive = filter === tab.value;
              const count = counts[tab.value] ?? 0;
              return (
                <button
                  key={tab.value}
                  onClick={() => setFilter(tab.value)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                    isActive
                      ? "bg-white text-aurora-navy shadow-sm"
                      : "text-neutral-600 hover:text-aurora-navy"
                  )}
                >
                  {tab.label}
                  <span
                    className={cn(
                      "ml-2 rounded-full px-1.5 py-0.5 text-xs font-medium",
                      isActive
                        ? "bg-[color-mix(in_srgb,var(--aurora-color-primary)_10%,transparent)] text-aurora-navy"
                        : "bg-neutral-200 text-neutral-500"
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, entreprise, sujet…"
                className="w-full rounded-md border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-neutral-400 focus:border-aurora-bright-blue focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--aurora-color-border-focus)_20%,transparent)]"
              />
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex size-9 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-500 transition-colors hover:border-aurora-bright-blue hover:text-aurora-bright-blue disabled:opacity-50"
              aria-label="Rafraîchir"
              title="Rafraîchir"
            >
              <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            </button>
          </div>
        </div>
      </div>

      {/* === Grille de tuiles === */}
      <div className="flex-1 overflow-auto bg-neutral-50">
        <div className="mx-auto max-w-7xl p-8">
          {isLoading ? (
            <SkeletonGrid />
          ) : filtered.length === 0 ? (
            <EmptyState filter={filter} hasAny={prospects.length > 0} search={search} />
          ) : (
            <>
              <div className="mb-4 text-sm text-neutral-500">
                {filtered.length} prospect{filtered.length > 1 ? "s" : ""}
                {filter !== "all" && filter !== "review" && (
                  <> · {PROSPECT_STATUS_LABELS[filter as ProspectStatus]}</>
                )}
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((p) => (
                  <ProspectTile key={p.id} prospect={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="aurora-tile-white h-56 animate-pulse p-5">
          <div className="mb-4 flex items-start justify-between">
            <div className="size-12 rounded-md bg-neutral-200" />
            <div className="h-5 w-20 rounded-full bg-neutral-200" />
          </div>
          <div className="mb-2 h-4 w-3/4 rounded bg-neutral-200" />
          <div className="mb-4 h-3 w-1/2 rounded bg-neutral-200" />
          <div className="h-12 rounded bg-neutral-100" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  filter,
  hasAny,
  search,
}: {
  filter: string;
  hasAny: boolean;
  search: string;
}) {
  let title: string;
  let description: string;
  if (!hasAny) {
    title = "Aucun prospect en base";
    description = "Lance le workflow d'ingestion sur n8n pour remplir la base depuis Apify.";
  } else if (search) {
    title = "Aucun résultat";
    description = `Aucun prospect ne matche « ${search} » dans le filtre "${filter}".`;
  } else {
    title = "Liste vide pour ce filtre";
    description =
      "Les prospects apparaîtront ici dès qu'ils atteindront cet état dans le pipeline.";
  }

  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-aurora-gray">
        <Inbox className="size-7 text-aurora-teal" />
      </div>
      <h2 className="mb-2 text-lg font-semibold text-aurora-navy">{title}</h2>
      <p className="text-sm text-neutral-600">{description}</p>
    </div>
  );
}
